import { FamilyAwardSeasonView, FamilyAwardEntryView, SaveFamilyAwardEntryInput, FamilyAwardVersionInput, } from '../../family-awards.js';
import { GroupChatVoteInput } from '../../chat.js';
import { communityRoutes } from '../community/routes.js';
import { z } from 'zod';
import { MarriageMilestoneView, SaveMarriageMilestoneInput, MarriageVersionInput, MarriageVerificationView, MarriagePublicView, MARRIAGE_VERIFICATION_MAX_BYTES, } from '../../marriage.js';
import { Paginated } from '../../common/pagination.js';
import { FamilyChatsQuery, FamilyChatView, FamilyChatMessageView, SendFamilyChatMessageInput, OpenFamilyPrivateChatInput, ReadFamilyChatInput, FamilyDeleteInput, FamilyDeletedView, CreateFamilyStoryInput, UpdateFamilyStoryInput, FamilyStoryView, CreateFamilyRequestInput, CorrectFamilyRequestInput, FamilyRequestActionInput, FamilyRequestView, FamilyMemberView, FamilySummary, FamilySuggestionView, FamilyListQuery, FamilyRequestsQuery, FamilyCircleRoleInput, FamilyCircleView, FamilyCircleMemberView, FamilyCircleSettingsInput, FamilyCircleMembersQuery, CreateFamilyInviteInput, CreatedFamilyInviteView, FamilyInviteView, FamilyInvitePreview, FamilyInviteTokenInput, } from '../../family.js';
import { CursorQuery } from '../../common/pagination.js';
export const familyRoutes = {
    seasonAwardEntry: {
        method: 'GET',
        path: '/family/awards/seasons/:seasonId/entry',
        response: FamilyAwardEntryView.nullable(),
    },
    awardSeason: {
        method: 'GET',
        path: '/family/awards/season',
        response: FamilyAwardSeasonView.nullable(),
    },
    awardEntries: {
        method: 'GET',
        path: '/family/awards/entries',
        query: CursorQuery,
        response: Paginated(FamilyAwardEntryView),
    },
    awardEntry: {
        method: 'GET',
        path: '/family/awards/entries/:entryId',
        response: FamilyAwardEntryView,
    },
    saveAwardEntry: {
        method: 'PUT',
        path: '/family/awards/entries',
        body: SaveFamilyAwardEntryInput,
        response: FamilyAwardEntryView,
    },
    confirmAwardEntry: {
        method: 'POST',
        path: '/family/awards/entries/:entryId/confirm',
        body: FamilyAwardVersionInput,
        response: FamilyAwardEntryView,
    },
    withdrawAwardEntry: {
        method: 'POST',
        path: '/family/awards/entries/:entryId/withdraw',
        body: FamilyAwardVersionInput,
        response: FamilyAwardEntryView,
    },
    // Meeting contracts are shared; family scope has separate authorization and storage.
    meetingAttendance: {
        ...communityRoutes.meetingAttendance,
        path: '/family/circles/:communityId/calendar/:meetingId/attendance',
    },
    pastMeetingPhotos: {
        ...communityRoutes.pastMeetingPhotos,
        path: '/family/circles/:communityId/calendar/photos',
    },
    nextMeeting: {
        ...communityRoutes.nextMeeting,
        path: '/family/circles/:communityId/calendar/next',
    },
    calendar: { ...communityRoutes.calendar, path: '/family/circles/:communityId/calendar' },
    createMeeting: {
        ...communityRoutes.createMeeting,
        path: '/family/circles/:communityId/meetings',
    },
    meeting: { ...communityRoutes.meeting, path: '/family/circles/:communityId/meetings/:meetingId' },
    updateMeeting: {
        ...communityRoutes.updateMeeting,
        path: '/family/circles/:communityId/meetings/:meetingId',
    },
    deleteMeeting: {
        ...communityRoutes.deleteMeeting,
        path: '/family/circles/:communityId/meetings/:meetingId',
    },
    rsvpMeeting: {
        ...communityRoutes.rsvpMeeting,
        path: '/family/circles/:communityId/meetings/:meetingId/rsvp',
    },
    marriage: { method: 'GET', path: '/family/marriage', response: MarriageMilestoneView.nullable() },
    saveMarriage: {
        method: 'PUT',
        path: '/family/marriage',
        body: SaveMarriageMilestoneInput,
        response: MarriageMilestoneView,
    },
    confirmMarriage: {
        method: 'POST',
        path: '/family/marriage/:marriageId/confirm',
        body: MarriageVersionInput,
        response: MarriageMilestoneView,
    },
    withdrawMarriage: {
        method: 'POST',
        path: '/family/marriage/:marriageId/withdraw',
        body: MarriageVersionInput,
        response: z.object({ withdrawn: z.literal(true) }),
    },
    uploadMarriageVerification: {
        method: 'POST',
        path: '/family/marriage/verification',
        upload: {
            field: 'file',
            accepts: ['application/pdf', 'image/jpeg', 'image/png'],
            maxBytes: MARRIAGE_VERIFICATION_MAX_BYTES,
        },
        response: MarriageVerificationView,
    },
    marriageStars: {
        method: 'GET',
        path: '/family/marriage/stars/:userId',
        response: MarriagePublicView.nullable(),
    },
    suggestions: {
        method: 'GET',
        path: '/family/suggestions',
        query: CursorQuery,
        response: Paginated(FamilySuggestionView),
    },
    privateChats: {
        method: 'GET',
        path: '/family/circles/:circleId/chat/private',
        query: FamilyChatsQuery,
        response: Paginated(FamilyChatView),
    },
    openPrivateChat: {
        method: 'POST',
        path: '/family/circles/:circleId/chat/private',
        body: OpenFamilyPrivateChatInput,
        response: FamilyChatView,
    },
    chatState: {
        method: 'GET',
        path: '/family/circles/:circleId/chat/:threadId',
        response: FamilyChatView,
    },
    chatMessages: {
        method: 'GET',
        path: '/family/circles/:circleId/chat/:threadId/messages',
        query: CursorQuery,
        response: Paginated(FamilyChatMessageView),
    },
    sendChatMessage: {
        method: 'POST',
        path: '/family/circles/:circleId/chat/:threadId/messages',
        body: SendFamilyChatMessageInput,
        response: FamilyChatMessageView,
    },
    voteChatPoll: {
        method: 'PUT',
        path: '/family/circles/:circleId/chat/:threadId/messages/:messageId/vote',
        body: GroupChatVoteInput,
        response: FamilyChatMessageView,
    },
    readChat: {
        method: 'PUT',
        path: '/family/circles/:circleId/chat/:threadId/read',
        body: ReadFamilyChatInput,
        response: FamilyChatView,
    },
    stories: {
        method: 'GET',
        path: '/family/circles/:circleId/stories',
        query: CursorQuery,
        response: Paginated(FamilyStoryView),
    },
    story: {
        method: 'GET',
        path: '/family/circles/:circleId/stories/:storyId',
        response: FamilyStoryView,
    },
    createStory: {
        method: 'POST',
        path: '/family/circles/:circleId/stories',
        body: CreateFamilyStoryInput,
        response: FamilyStoryView,
    },
    updateStory: {
        method: 'PATCH',
        path: '/family/circles/:circleId/stories/:storyId',
        body: UpdateFamilyStoryInput,
        response: FamilyStoryView,
    },
    deleteStory: {
        method: 'DELETE',
        path: '/family/circles/:circleId/stories/:storyId',
        body: FamilyDeleteInput,
        response: FamilyDeletedView,
    },
    circleMemberRole: {
        method: 'PATCH',
        path: '/family/circles/:circleId/members/:userId/role',
        body: FamilyCircleRoleInput,
        response: FamilyCircleView,
    },
    circles: {
        method: 'GET',
        path: '/family/circles',
        query: FamilyListQuery,
        response: Paginated(FamilyCircleView),
    },
    circle: { method: 'GET', path: '/family/circles/:circleId', response: FamilyCircleView },
    circleMembers: {
        method: 'GET',
        path: '/family/circles/:circleId/members',
        query: FamilyCircleMembersQuery,
        response: Paginated(FamilyCircleMemberView),
    },
    circleSettings: {
        method: 'PATCH',
        path: '/family/circles/:circleId',
        body: FamilyCircleSettingsInput,
        response: FamilyCircleView,
    },
    approveCircleMember: {
        method: 'POST',
        path: '/family/circles/:circleId/members/:userId/approve',
        response: FamilyCircleView,
    },
    createInvite: {
        method: 'POST',
        path: '/family/invites',
        body: CreateFamilyInviteInput,
        response: CreatedFamilyInviteView,
    },
    invites: {
        method: 'GET',
        path: '/family/invites',
        query: CursorQuery,
        response: Paginated(FamilyInviteView),
    },
    revokeInvite: { method: 'DELETE', path: '/family/invites/:inviteId', response: FamilyInviteView },
    previewInvite: {
        method: 'POST',
        path: '/family/invites/preview',
        body: FamilyInviteTokenInput,
        response: FamilyInvitePreview,
    },
    claimInvite: {
        method: 'POST',
        path: '/family/invites/claim',
        body: FamilyInviteTokenInput,
        response: FamilyRequestView,
    },
    summary: { method: 'GET', path: '/family/summary', response: FamilySummary },
    members: {
        method: 'GET',
        path: '/family/members',
        query: FamilyListQuery,
        response: Paginated(FamilyMemberView),
    },
    requests: {
        method: 'GET',
        path: '/family/requests',
        query: FamilyRequestsQuery,
        response: Paginated(FamilyRequestView),
    },
    request: { method: 'GET', path: '/family/requests/:requestId', response: FamilyRequestView },
    createRequest: {
        method: 'POST',
        path: '/family/requests',
        body: CreateFamilyRequestInput,
        response: FamilyRequestView,
    },
    accept: {
        method: 'POST',
        path: '/family/requests/:requestId/accept',
        body: FamilyRequestActionInput,
        response: FamilyRequestView,
    },
    correct: {
        method: 'POST',
        path: '/family/requests/:requestId/correct',
        body: CorrectFamilyRequestInput,
        response: FamilyRequestView,
    },
    decline: {
        method: 'POST',
        path: '/family/requests/:requestId/decline',
        body: FamilyRequestActionInput,
        response: FamilyRequestView,
    },
    cancel: {
        method: 'POST',
        path: '/family/requests/:requestId/cancel',
        body: FamilyRequestActionInput,
        response: FamilyRequestView,
    },
    undoDecline: {
        method: 'POST',
        path: '/family/requests/:requestId/undo',
        body: FamilyRequestActionInput,
        response: FamilyRequestView,
    },
    removeMember: {
        method: 'DELETE',
        path: '/family/members/:requestId',
        body: FamilyRequestActionInput,
        response: FamilyRequestView,
    },
};
//# sourceMappingURL=routes.js.map